/**
 * Coloured regions **inside** the CEC structure triangle.
 *
 * Geometry is copied from the historical Structure Triangle asset
 * (viewBox 0 0 842 842) — only fills are themed.
 *
 * Each region has:
 *   - `id`     stable key
 *   - `role`   structure class (maps to chartTheme.structure*)
 *   - `d`      SVG path (frozen geometry)
 *
 * Paint bottom → top so smaller “better” cores sit on the body.
 */
export const STRUCTURE_ZONE_VIEWBOX = 842;

export type StructureRegionRole =
	| "moderate"
	| "moderateSoft"
	| "poor"
	| "reasonable"
	| "good"
	| "optimal";

export interface StructureRegion {
	id: string;
	/** Which chartTheme colour slot to use. */
	role: StructureRegionRole;
	/** Exact path `d` from the structure-triangle asset. */
	d: string;
	/** Optional hairline for small cores. */
	stroke?: boolean;
}

/**
 * Identified coloured regions (inside the triangle).
 * Order = paint order (later draws on top).
 */
export const STRUCTURE_REGIONS: readonly StructureRegion[] = [
	// ── Body (moderate structure) ────────────────────────────────────
	{
		id: "body",
		role: "moderate",
		// Full equilateral triangle: Ca top · Residual BL · Mg BR
		d: "m43.87 562.74h736.71l-372.3-426.84z",
	},
	// Soft accents still “moderate” family (same school as body)
	{
		id: "residual-accent",
		role: "moderateSoft",
		d: "m292.05 364.77l-159.55 197.3-90.73 0.54 170.65-197.22z",
	},
	{
		id: "ca-apex-accent",
		role: "moderateSoft",
		d: "m428.65 159.26c-2.28-2.99-20.37-23.37-20.37-23.37l-294.42 343.42c0 0 216.96-251.86 275.01-319.92 0 0 39.89 0.01 39.78-0.13z",
	},
	{
		id: "mg-pocket",
		role: "moderate",
		d: "m589.39 376.96c-26.38-24.29-75.03-71.85-86.9-133.08l110.47 126.59c0 0-6.01 22.65-23.57 6.49z",
	},
	{
		id: "mg-fragment",
		role: "moderate",
		d: "m613.5 371.19l0.89 12.6-27 2.75 2.06-13.49-85.9-127.05z",
	},

	// ── Poor structure — base band ───────────────────────────────────
	{
		id: "poor-base",
		role: "poor",
		d: "m780.45 562.61c0 0-0.05-0.12-0.21-0.3-38.92-45.06-75.76-86.66-109.85-125.84-67.79-47.47-142.99-53.34-249.01-51.74-102.34 1.74-199.02 3.96-270.32 51.09-29.4 35.1-74.3 86-108.5 126.05q-0.4 0.73-0.4 0.73z",
	},

	// ── Reasonable structure — upper Ca region ───────────────────────
	{
		id: "reasonable",
		role: "reasonable",
		d: "m530.74 276.47c39.67 45.87 59.45 100.77 58.16 99.83-18.07-13.13-39.2-9.57-66.38-13.76-20.56-3.17-44.27-15.59-74.43-15.59-30.95 0-76.41 14.81-99.96 19.14-56.83 10.45-138.49 1.98-138.49 1.98l198.15-232.54c0 0 63.82 72.58 122.95 140.94z",
	},

	// ── Good structure — ellipse around optimal ──────────────────────
	{
		id: "good",
		role: "good",
		d: "m466.84 253.37c-10.27 26.72-39.66 39.84-61.66 29.5-21.99-10.32-33.09-41.96-22.83-68.69 10.27-26.72 38.02-38.44 60.02-28.1 21.99 10.33 34.73 40.56 24.47 67.29z",
		stroke: true,
	},

	// ── Optimal structure — core circle ──────────────────────────────
	{
		id: "optimal",
		role: "optimal",
		d: "m430.81 229.58c-15.13 0-27.35-10.04-27.35-22.47 0-12.42 12.22-22.46 27.35-22.46 15.13 0 27.35 10.04 27.35 22.46 0 12.43-12.22 22.47-27.35 22.47z",
		stroke: true,
	},
] as const;

/** Triangle outline only (same as body path). */
export const TRIANGLE_OUTLINE_D = STRUCTURE_REGIONS[0]!.d;
